import { forwardRef, useId } from 'react'
import { cn } from '../../lib/cn'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helpText?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, className, id, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id || generatedId
    const describedBy = error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-text">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'block w-full rounded-control bg-surface-sunken px-3 py-2.5 text-base text-text',
            'ring-1 ring-border transition duration-200 ease-out placeholder:text-text-faint',
            'focus:outline-none focus:ring-2 focus:ring-ember-400/50',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error && 'ring-danger/60 focus:ring-danger/60',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...props}
        />
        {helpText && !error && (
          // muted, not faint: help text is a real instruction (WCAG 1.4.3).
          <p id={`${inputId}-help`} className="text-xs text-text-muted">
            {helpText}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-danger-fg">
            {error}
          </p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
